import React from 'react';
import { withTranslation, WithTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { match } from 'react-router-dom';

// Components
import App from '../../../components/app';
import ErrorMessage from '../../../components/error-message';
import EnhancedForm from '../../../components/form';
import { FormDatas } from '../../../local-types';
// Modules
import { Manager, TParams } from '../../../redux/reducers/api-types';
import { ErrorPayload, selectors as errorSelectors } from '../../../redux/reducers/errors';
import {
    actions as partnerActions,
    selectors as partnerSelectors,
    types as partnerTypes,
} from '../../../redux/reducers/partners';
import { selectors as pendingSelectors } from '../../../redux/reducers/pendingOperations';
// Ressources
import ObjectUtils from '../../../utils/objectUtils';
import { AccountRight, RightRank } from '../../../utils/userUtils';
import { createPath, paths } from '../../paths';

interface PartnerManagerEditProps extends WithTranslation {
    match: match<TParams>;
    managers: Manager[];
    editPartnerManagerError: ErrorPayload;
    getManager: (id: string, idm: string) => void;
    editManager: (id: string, idm: string, payload: any) => void;
    isEditingManager: boolean;
}

interface PartnerManagerEditState {
    formData: any;
}

class PartnerManagerEditPage extends React.Component<PartnerManagerEditProps, PartnerManagerEditState> {
    state = {
        formData: {},
    };

    componentDidMount() {
        const { match, managers, getManager } = this.props;
        const { id, idm } = match.params;

        if (!managers.find((m) => String(m.id) === idm)) {
            getManager(id, idm);
        }
    }

    static getDerivedStateFromProps(props, state) {
        const { match, managers } = props;
        const { idm } = match.params;
        const payload = managers.find((m) => String(m.id) === idm);
        if (payload) {
            return { formData: payload };
        }
        return null;
    }

    //========================================//
    //=============== Builders ===============//
    //========================================//

    generateInputs = (): FormDatas[] => {
        const { t } = this.props;
        const inputs: FormDatas[] = [
            {
                title: t('add_manager.title'),
                datas: [
                    { id: 'lastname', label: t('common:form.lastname'), required: true },
                    {
                        id: 'firstname',
                        label: t('common:form.firstname'),
                        required: true,
                    },
                    {
                        id: 'email',
                        label: t('common:form.email'),
                        hint: t('common:form.email_hint'),
                        type: 'email',
                        required: true,
                    },
                    {
                        id: 'phone_number',
                        label: t('common:form.phone_number'),
                        type: 'tel',
                        required: true,
                    },
                    {
                        id: 'banned',
                        label: t('common:suspended'),
                        type: 'boolean',
                        required: true,
                    },
                ],
            },
        ];
        return inputs;
    };

    onSubmitManager = (formData: any, event): void => {
        const { editManager, match } = this.props;
        const { id, idm } = match.params;
        this.setState({
            formData: formData,
        });
        editManager(id, idm, formData);
    };

    render() {
        const { editPartnerManagerError, isEditingManager, match } = this.props;
        const { id, idm } = match.params;

        const formPayload = this.state.formData;

        return (
            <App needRights={{ name: AccountRight.PARTNER_SETTINGS, mode: RightRank.W }}>
                <ErrorMessage payload={editPartnerManagerError} />
                {!ObjectUtils.isEmpty(formPayload) && (
                    <>
                        <EnhancedForm
                            payload={formPayload}
                            inputs={this.generateInputs()}
                            backRoute={createPath(paths.partnersManagers, { id, idm })}
                            onSubmit={this.onSubmitManager}
                            loader={isEditingManager}
                        />
                    </>
                )}
            </App>
        );
    }
}

const PartnerManagerEditPageWithTranslation = withTranslation(['partner', 'common'])(PartnerManagerEditPage);

const mapStateToProps = (state, props) => ({
    managers: partnerSelectors.partnersManagersWithPartner(state, props?.match?.params?.id),
    editPartnerManagerError: errorSelectors.editPartnerManagerError(state),
    isEditingManager: pendingSelectors.isPending(state, partnerTypes.EDIT_PARTNER_MANAGER),
});

const mapDispatchToProps = {
    getManager: partnerActions.getManager,
    editManager: partnerActions.editManager,
};

export default connect(mapStateToProps, mapDispatchToProps)(PartnerManagerEditPageWithTranslation);
